import React from 'react';

/**
 * Error Boundary pour capturer les erreurs React et éviter le crash complet de l'application
 * Affiche une interface de secours élégante en cas d'erreur
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Mise à jour de l'état pour afficher l'UI de fallback
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log l'erreur pour le debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Vous pouvez aussi envoyer l'erreur à un service de monitoring
    // comme Sentry, LogRocket, etc.
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Interface de secours élégante
      return (
        <div
          style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #1a1d24 0%, #2a2f3a 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
          <div
            style={{
              maxWidth: '500px',
              width: '100%',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '40px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              textAlign: 'center',
            }}
          >
            {/* Icône d'erreur */}
            <div
              style={{
                fontSize: '64px',
                marginBottom: '20px',
              }}
            >
              ⚠️
            </div>

            {/* Titre */}
            <h1
              style={{
                color: '#ffffff',
                fontSize: '28px',
                fontWeight: '700',
                marginBottom: '12px',
              }}
            >
              Une erreur est survenue
            </h1>

            {/* Description */}
            <p
              style={{
                color: '#e9ecef',
                fontSize: '16px',
                lineHeight: '1.6',
                marginBottom: '30px',
              }}
            >
              Nous sommes désolés, quelque chose s'est mal passé. Veuillez recharger la page ou réessayer plus tard.
            </p>

            {/* Détails de l'erreur (en développement) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details
                style={{
                  marginBottom: '30px',
                  textAlign: 'left',
                  background: 'rgba(0, 0, 0, 0.2)',
                  padding: '15px',
                  borderRadius: '12px',
                  color: '#adb5bd',
                  fontSize: '14px',
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                <summary style={{ cursor: 'pointer', marginBottom: '10px', fontWeight: '600' }}>
                  Détails techniques
                </summary>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                  {this.state.error.toString()}
                  {'\n\n'}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            {/* Boutons d'action */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '14px 28px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#ffffff',
                  background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 16px rgba(74, 144, 226, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(74, 144, 226, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 16px rgba(74, 144, 226, 0.3)';
                }}
              >
                Recharger la page
              </button>

              {process.env.NODE_ENV === 'development' && (
                <button
                  onClick={this.handleReset}
                  style={{
                    padding: '14px 28px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#ffffff',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  }}
                >
                  Réessayer
                </button>
              )}
            </div>

            {/* Message de support */}
            <p
              style={{
                marginTop: '30px',
                color: '#adb5bd',
                fontSize: '14px',
              }}
            >
              Si le problème persiste, contactez le support technique.
            </p>
          </div>
        </div>
      );
    }

    // Rendu normal des enfants
    return this.props.children;
  }
}

export default ErrorBoundary;
